import * as React from 'react'
import { format, parseISO } from 'date-fns'
import { Calendar as CalendarIcon } from 'lucide-react'
import { toCalendarDay, type CalendarDay } from '@lib/validators/temporal.validator'
import { cn } from '@/lib/utils'
import { PICKER_CLASSES } from './input'
import { Popover, PopoverTrigger, PopoverContent } from './popover'
import { Calendar } from './calendar'

interface DatePickerProps {
  /** The selected date as `yyyy-MM-dd`, or '' for none. */
  value: CalendarDay | ''
  /** Called with the new `yyyy-MM-dd` (or '' if cleared). */
  onChange: (value: CalendarDay | '') => void
  placeholder?: string
  disabled?: boolean
  id?: string
  /**
   * Days to mark with a small square (`yyyy-MM-dd`) — the days another event
   * already holds this track, say — and what the mark means, written under the
   * grid. Plan View: a clash should show before the server rejects it.
   */
  marked?: readonly CalendarDay[] | undefined
  markedLabel?: string | undefined
  /**
   * Days held for another couple's inquiry, and what that means. Drawn as a
   * dashed outline: spoken for, not taken. A warning only — the day can still
   * be picked.
   */
  held?: readonly CalendarDay[] | undefined
  heldLabel?: string | undefined
  /**
   * Called with the first day of the month the calendar turns to, so a caller
   * can load that month's marks.
   */
  onMonthChange?: ((month: CalendarDay) => void) | undefined
  /** The earliest and latest days that can be chosen (`yyyy-MM-dd`). */
  min?: CalendarDay | undefined
  max?: CalendarDay | undefined
  className?: string | undefined
}

/**
 * A checked day as the local midnight `react-day-picker` compares against.
 *
 * `parseISO` reads a date-only string as local midnight — not UTC midnight,
 * which is the previous day west of Greenwich.
 */
function localDay(day: CalendarDay): Date {
  return parseISO(day)
}

/** The `yyyy-MM-dd` a picker date is, or null when it is not a real day. */
function dayOf(date: Date): CalendarDay | null {
  return toCalendarDay(format(date, 'yyyy-MM-dd'))
}

/** A calendar date picker: a button showing the chosen date opens a popover
 * calendar. Works in `yyyy-MM-dd` strings to match the API's date fields. */
export function DatePicker({
  value,
  onChange,
  placeholder = 'Pick a date',
  disabled,
  id,
  marked,
  markedLabel,
  held,
  heldLabel,
  onMonthChange,
  min,
  max,
  className,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false)
  const selected = value === '' ? undefined : localDay(value)
  const markedDates = React.useMemo(() => (marked ?? []).map((day) => localDay(day)), [marked])
  const heldDates = React.useMemo(() => (held ?? []).map((day) => localDay(day)), [held])
  const showLegend = markedLabel !== undefined && markedDates.length > 0
  const showHeldLegend = heldLabel !== undefined && heldDates.length > 0
  const minDate = min === undefined ? undefined : localDay(min)
  const maxDate = max === undefined ? undefined : localDay(max)
  const outOfRange = [...(minDate ? [{ before: minDate }] : []), ...(maxDate ? [{ after: maxDate }] : [])]

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          className={cn(PICKER_CLASSES, !selected && 'text-ink-muted', className)}
        >
          <CalendarIcon className="h-4 w-4 shrink-0 text-ink-muted" />
          {selected ? format(selected, 'MMM d, yyyy') : placeholder}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start">
        <Calendar
          mode="single"
          onSelect={(date) => {
            if (date === undefined) {
              onChange('')
              setOpen(false)
              return
            }
            const day = dayOf(date)
            if (day === null) return
            onChange(day)
            setOpen(false)
          }}
          modifiers={{ marked: markedDates, held: heldDates }}
          modifiersClassNames={{ marked: 'ds-cal-marked', held: 'ds-cal-held' }}
          onMonthChange={(month) => {
            if (onMonthChange === undefined) return
            const day = dayOf(month)
            if (day !== null) onMonthChange(day)
          }}
          disabled={outOfRange}
          {...(minDate ? { startMonth: minDate } : {})}
          {...(maxDate ? { endMonth: maxDate } : {})}
          {...(selected ? { selected, defaultMonth: selected } : {})}
        />
        {showLegend || showHeldLegend ? (
          <div className="ds-cal-legend">
            {showLegend ? (
              <p className="ds-cal-legend__item">
                <span aria-hidden className="ds-cal-legend__mark" />
                {markedLabel}
              </p>
            ) : null}
            {showHeldLegend ? (
              <p className="ds-cal-legend__item">
                <span aria-hidden className="ds-cal-legend__mark ds-cal-legend__mark--held" />
                {heldLabel}
              </p>
            ) : null}
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}
